import {
  Children,
  Fragment,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import { FieldShell } from './FieldShell'
import { FloatingPanel } from './FloatingPanel'

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label: string
  variant?: 'outlined' | 'filled' | undefined
  supportingText?: ReactNode | undefined
  error?: string | undefined
  labelHidden?: boolean | undefined
  leadingIcon?: string | undefined
  trailingIcon?: string | undefined
  children: ReactNode
}

interface Opt {
  value: string
  label: string
  disabled: boolean
  group: string | undefined
}

function textOf(node: ReactNode): string {
  return Children.toArray(node)
    .map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : isValidElement(c) ? textOf((c.props as { children?: ReactNode }).children) : ''))
    .join('')
}

/** Legge <option>/<optgroup> (anche in Fragment e array) come dati. */
function collect(children: ReactNode, group: string | undefined, out: Opt[]): Opt[] {
  Children.forEach(children, (c) => {
    if (!isValidElement(c)) return
    const el = c as ReactElement<{ value?: string | number | readonly string[]; disabled?: boolean; label?: string; children?: ReactNode }>
    if (el.type === 'option') {
      const text = textOf(el.props.children)
      const v = el.props.value
      out.push({ value: v === undefined ? text : String(v), label: el.props.label ?? text, disabled: Boolean(el.props.disabled), group })
    } else if (el.type === 'optgroup') {
      collect(el.props.children, el.props.label, out)
    } else if (el.type === Fragment) {
      collect(el.props.children, group, out)
    }
  })
  return out
}

const isBlank = (o: Opt | undefined) => !o || (o.value === '' && /^[\s—–-]*$/.test(o.label))

function setNativeValue(sel: HTMLSelectElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set
  setter?.call(sel, value)
  sel.dispatchEvent(new Event('change', { bubbles: true }))
}

/**
 * Exposed dropdown menu M3 (combobox select-only WAI-ARIA). Sotto c'e' una <select> vera, nascosta ma presente:
 * porta name/required/FormData e l'evento change che le pagine gia' leggono (e.target.value).
 */
export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  {
    label,
    variant = 'outlined',
    supportingText,
    error,
    leadingIcon,
    labelHidden,
    id: idProp,
    className,
    disabled,
    children,
    onFocus,
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const autoId = useId()
  const id = idProp ?? autoId
  const msgId = `${id}-msg`
  const listId = `${id}-list`
  const labelId = `${id}-label`
  const hasMessage = Boolean(error || supportingText)
  const describedBy = [ariaDescribedBy, hasMessage ? msgId : undefined].filter(Boolean).join(' ') || undefined

  const selRef = useRef<HTMLSelectElement | null>(null)
  const comboRef = useRef<HTMLDivElement | null>(null)
  const boxRef = useRef<HTMLDivElement | null>(null)
  const typed = useRef({ text: '', at: 0 })
  const [cur, setCur] = useState<string>('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)

  const opts = collect(children, undefined, [])
  const selected = opts.find((o) => o.value === cur)
  const display = isBlank(selected) ? '' : (selected?.label ?? '')

  // La <select> nativa e' la fonte del valore (controllata o no): la rilegge a ogni render.
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const v = selRef.current?.value ?? ''
    if (v !== cur) setCur(v)
  })
  useEffect(() => {
    const form = selRef.current?.form
    if (!form) return undefined
    const onReset = () => setTimeout(() => setCur(selRef.current?.value ?? ''), 0)
    form.addEventListener('reset', onReset)
    return () => form.removeEventListener('reset', onReset)
  }, [])

  const setRefs = (el: HTMLSelectElement | null) => {
    selRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) ref.current = el
  }

  const enabled = (i: number) => i >= 0 && i < opts.length && !opts[i]?.disabled
  const step = (from: number, dir: 1 | -1) => {
    for (let k = 1; k <= opts.length; k += 1) {
      const i = from + dir * k
      if (i < 0 || i >= opts.length) return from
      if (enabled(i)) return i
    }
    return from
  }
  const first = () => opts.findIndex((_, i) => enabled(i))
  const last = () => {
    for (let i = opts.length - 1; i >= 0; i -= 1) if (enabled(i)) return i
    return -1
  }

  const openList = (at?: 'first' | 'last') => {
    if (disabled) return
    const sel = opts.findIndex((o) => o.value === cur)
    setActive(at === 'first' ? first() : at === 'last' ? last() : sel >= 0 && enabled(sel) ? sel : first())
    setOpen(true)
  }
  const closeList = useCallback(() => setOpen(false), [])
  const choose = (i: number) => {
    const o = opts[i]
    setOpen(false)
    if (!o || o.disabled) return
    const sel = selRef.current
    if (sel && sel.value !== o.value) setNativeValue(sel, o.value)
    setCur(o.value)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled || e.ctrlKey || e.metaKey) return
    const k = e.key
    if (!open) {
      if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || k === ' ') {
        e.preventDefault()
        openList()
      } else if (k === 'Home') {
        e.preventDefault()
        openList('first')
      } else if (k === 'End') {
        e.preventDefault()
        openList('last')
      } else if (k.length === 1 && !e.altKey) {
        e.preventDefault()
        openList()
        typeahead(k)
      }
      return
    }
    if (k === 'ArrowDown') {
      e.preventDefault()
      if (e.altKey) return
      setActive((a) => (a < 0 ? first() : step(a, 1)))
    } else if (k === 'ArrowUp') {
      e.preventDefault()
      if (e.altKey) {
        choose(active)
        return
      }
      setActive((a) => (a < 0 ? last() : step(a, -1)))
    } else if (k === 'Home') {
      e.preventDefault()
      setActive(first())
    } else if (k === 'End') {
      e.preventDefault()
      setActive(last())
    } else if (k === 'PageDown') {
      e.preventDefault()
      setActive((a) => Math.min(last(), Math.max(a, 0) + 10))
    } else if (k === 'PageUp') {
      e.preventDefault()
      setActive((a) => Math.max(first(), a - 10))
    } else if (k === 'Enter' || k === ' ') {
      e.preventDefault()
      choose(active)
    } else if (k === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
    } else if (k === 'Tab') {
      if (enabled(active)) choose(active)
      else setOpen(false)
    } else if (k.length === 1 && !e.altKey) {
      e.preventDefault()
      typeahead(k)
    }
  }

  function typeahead(key: string) {
    const now = Date.now()
    const t = typed.current
    t.text = now - t.at > 700 ? key.toLowerCase() : t.text + key.toLowerCase()
    t.at = now
    const startAt = t.text.length === 1 ? active + 1 : Math.max(active, 0)
    for (let k = 0; k < opts.length; k += 1) {
      const i = (startAt + k) % opts.length
      if (enabled(i) && opts[i]?.label.trim().toLowerCase().startsWith(t.text)) {
        setActive(i)
        return
      }
    }
  }

  useEffect(() => {
    if (!open || active < 0) return
    document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: 'nearest' })
  }, [open, active, listId])

  const optId = (i: number) => `${listId}-${i}`
  let lastGroup: string | undefined
  const rows: ReactNode[] = []
  opts.forEach((o, i) => {
    if (o.group !== lastGroup) {
      lastGroup = o.group
      if (o.group) rows.push(<div key={`g${i}`} role="presentation" className="md-listbox__group">{o.group}</div>)
    }
    const isSel = o.value === cur
    rows.push(
      <div
        key={`o${i}`}
        id={optId(i)}
        role="option"
        aria-selected={isSel}
        aria-disabled={o.disabled || undefined}
        className={cx('md-listbox__option', isSel && 'md-listbox__option--selected', i === active && 'md-listbox__option--active', o.disabled && 'md-listbox__option--disabled')}
        onMouseDown={(e) => e.preventDefault()}
        onMouseMove={() => { if (!o.disabled && active !== i) setActive(i) }}
        onClick={() => choose(i)}
      >
        <span className="md-listbox__label">{isBlank(o) ? 'Nessuno' : o.label}</span>
        {isSel ? <Icon name="check" size={24} className="md-listbox__check" /> : null}
      </div>,
    )
  })

  return (
    <FieldShell
      id={id}
      labelId={labelId}
      containerRef={boxRef}
      label={label}
      labelHidden={labelHidden}
      variant={variant}
      supportingText={supportingText}
      error={error}
      disabled={disabled}
      floated={display !== '' || open}
      leadingIcon={leadingIcon}
      trailingIcon="arrow_drop_down"
      prefix={undefined}
      suffix={undefined}
      multiline={false}
      className={cx('md-field--select', 'md-field--combo', open && 'md-field--open', className)}
      describedBy={hasMessage ? msgId : undefined}
    >
      <div
        ref={comboRef}
        id={`${id}-cb`}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && active >= 0 ? optId(active) : undefined}
        aria-labelledby={labelId}
        aria-required={rest.required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        aria-disabled={disabled || undefined}
        className="md-field__input md-field__combo"
        onKeyDown={onKeyDown}
        onClick={() => (open ? setOpen(false) : openList())}
      >
        {display || ' '}
      </div>
      <select
        {...rest}
        ref={setRefs}
        id={id}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        className="md-field__native"
        onFocus={(e) => {
          onFocus?.(e)
          comboRef.current?.focus()
        }}
      >
        {children}
      </select>
      <FloatingPanel
        anchorRef={boxRef}
        open={open}
        onClose={closeList}
        matchWidth
        id={listId}
        role="listbox"
        aria-label={label}
        className="md-listbox"
      >
        {rows}
      </FloatingPanel>
    </FieldShell>
  )
})
